import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import crm from './api';

// Kept in step with what demo mode can actually answer (server/routes/chat.js).
// These double as the demo script — they're the questions that land.
const SUGGESTIONS = [
  "How am I doing?",
  'Which of my leads are hot?',
  'Where do my leads come from?',
  'What products are people asking about?',
  "What's my biggest deal?",
  'What follow-ups are due today?',
];

export default function ChatPanel() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, busy]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    const next = [...msgs, { role: 'user', content: q }];
    setMsgs(next);
    setBusy(true);
    try {
      // Send only text turns — the server owns the tool plumbing.
      const { data } = await crm.post('/chat', {
        messages: next.map(({ role, content }) => ({ role, content })),
      });
      setDemo(!!data.demo);
      setMsgs([...next, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setMsgs([...next, {
        role: 'assistant',
        error: true,
        content: e.response?.data?.error || 'Chat failed. Is the CRM backend running?',
      }]);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open lead assistant"
        style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 60,
          width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: 'var(--acc, #1D4E89)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(12,35,64,.32)',
        }}
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed', right: 24, bottom: 24, zIndex: 60,
        width: 'min(420px, calc(100vw - 32px))', height: 'min(560px, calc(100vh - 48px))',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: 'var(--card, #fff)', border: '1px solid var(--border, #d2dce8)',
        borderRadius: 16, boxShadow: '0 24px 60px rgba(12,35,64,.28)',
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
        background: 'var(--acc, #1D4E89)', color: '#fff', flexShrink: 0,
      }}>
        <MessageCircle size={18} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>Lead Assistant</div>
          <div style={{ fontSize: 11, opacity: 0.75 }}>
            {demo ? 'Demo mode · real data, canned replies' : 'Ask about your leads · read-only'}
          </div>
        </div>
        <button onClick={() => setOpen(false)} aria-label="Close lead assistant"
          style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'flex' }}>
          <X size={18} />
        </button>
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, background: 'var(--bg-a, #f2f5f9)' }}>
        {!msgs.length && (
          <div style={{ color: 'var(--muted, #6e86a3)', fontSize: 13 }}>
            <p style={{ marginBottom: 12, lineHeight: 1.5 }}>
              Ask me anything about your leads — details, contact numbers, stage, follow-ups.
            </p>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 6,
                  padding: '8px 10px', fontSize: 12.5, cursor: 'pointer',
                  background: 'var(--card, #fff)', color: 'var(--text, #0c2340)',
                  border: '1px solid var(--border, #d2dce8)', borderRadius: 8,
                }}>
                {s}
              </button>
            ))}
          </div>
        )}

        {msgs.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
            <div style={{
              maxWidth: '85%', padding: '9px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.55,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              background: m.role === 'user' ? 'var(--acc, #1D4E89)' : m.error ? '#fef2f2' : 'var(--card, #fff)',
              color: m.role === 'user' ? '#fff' : m.error ? '#991b1b' : 'var(--text, #0c2340)',
              border: m.role === 'user' ? 'none' : `1px solid ${m.error ? '#fecaca' : 'var(--border, #d2dce8)'}`,
            }}>
              {m.content}
            </div>
          </div>
        ))}

        {busy && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted, #6e86a3)', fontSize: 12.5 }}>
            <Loader2 size={14} className="animate-spin" /> Looking that up…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--border, #d2dce8)', background: 'var(--card, #fff)', flexShrink: 0 }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your leads…"
          disabled={busy}
          style={{
            flex: 1, padding: '9px 12px', fontSize: 13, borderRadius: 8, outline: 'none',
            border: '1px solid var(--border, #d2dce8)', background: 'var(--bg-a, #f2f5f9)', color: 'var(--text, #0c2340)',
          }}
        />
        <button type="submit" disabled={busy || !input.trim()} aria-label="Send"
          style={{
            padding: '0 14px', borderRadius: 8, border: 'none', cursor: busy || !input.trim() ? 'default' : 'pointer',
            background: 'var(--acc, #1D4E89)', color: '#fff', opacity: busy || !input.trim() ? 0.5 : 1,
            display: 'flex', alignItems: 'center',
          }}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
